<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES } from '../constants.js';

const store = useStore();
const {
  content,
  contentAttributes,
  messageType,
  id: messageId,
  conversationId,
} = useMessageContext();

// Vote state
const selectedOptions = ref([]);
const hasVoted = ref(false);
const isVoting = ref(false);

const pollData = computed(() => {
  const {
    question,
    options = [],
    is_anonymous,
    allows_multiple_answers,
    type,
    poll_id,
  } = contentAttributes.value || {};

  return {
    question: question || content.value,
    options: options,
    isAnonymous: is_anonymous !== false,
    allowsMultiple: allows_multiple_answers === true,
    isQuiz: type === 'quiz',
    pollId: poll_id,
  };
});

const pollIcon = computed(() => {
  return pollData.value.allowsMultiple ? '☑' : '○';
});

// Only allow voting on incoming polls (polls received from customer)
const canVote = computed(() => {
  return messageType.value === MESSAGE_TYPES.INCOMING && !hasVoted.value;
});

const isOptionSelected = index => {
  return selectedOptions.value.includes(index);
};

const toggleOption = index => {
  if (!canVote.value) return;

  if (pollData.value.allowsMultiple) {
    // Multiple selection: toggle
    const idx = selectedOptions.value.indexOf(index);
    if (idx > -1) {
      selectedOptions.value.splice(idx, 1);
    } else {
      selectedOptions.value.push(index);
    }
  } else {
    // Single selection: replace
    selectedOptions.value = [index];
  }
};

const canSubmitVote = computed(() => {
  return selectedOptions.value.length > 0 && !isVoting.value;
});

const submitVote = async () => {
  if (!canSubmitVote.value) return;

  isVoting.value = true;

  try {
    // Get selected option texts
    const selectedOptionTexts = selectedOptions.value.map(
      idx => pollData.value.options[idx]
    );

    // Create vote message
    const voteMessage =
      selectedOptionTexts.length > 1
        ? `Voted: ${selectedOptionTexts.join(', ')}`
        : `Voted: ${selectedOptionTexts[0]}`;

    // Send vote as a message
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: conversationId.value,
      content: voteMessage,
      private: false,
      content_type: 'text',
      content_attributes: {
        in_reply_to: messageId.value,
        poll_vote: true,
        poll_id: pollData.value.pollId,
        option_ids: selectedOptions.value,
        selected_options: selectedOptionTexts,
      },
    });

    hasVoted.value = true;
    useAlert('Vote submitted successfully');
  } catch (error) {
    console.error('Failed to submit vote:', error);
    useAlert('Failed to submit vote');
  } finally {
    isVoting.value = false;
  }
};
</script>

<template>
  <BaseBubble class="px-4 py-3">
    <!-- Poll Question -->
    <div class="flex items-start gap-2 mb-3">
      <span class="text-lg">📊</span>
      <div class="font-medium text-sm text-n-slate-12">
        {{ pollData.question }}
      </div>
    </div>

    <!-- Poll Options -->
    <div class="space-y-2">
      <button
        v-for="(option, index) in pollData.options"
        :key="index"
        type="button"
        :disabled="!canVote"
        class="w-full flex items-center gap-2 px-3 py-2 border rounded-lg transition-all text-left"
        :class="{
          'border-n-blue-7 bg-n-blue-2': isOptionSelected(index) && canVote,
          'border-n-slate-6 bg-n-slate-1': !isOptionSelected(index) && canVote,
          'border-n-slate-6 bg-n-slate-2 opacity-60': !canVote,
          'cursor-pointer hover:border-n-blue-6 hover:bg-n-blue-1': canVote,
          'cursor-not-allowed': !canVote,
        }"
        @click="toggleOption(index)"
      >
        <span
          class="text-sm flex-shrink-0"
          :class="{
            'text-n-blue-9': isOptionSelected(index) && canVote,
            'text-n-slate-10': !isOptionSelected(index) || !canVote,
          }"
        >
          {{
            isOptionSelected(index)
              ? pollData.allowsMultiple
                ? '✓'
                : '●'
              : pollIcon
          }}
        </span>
        <span
          class="text-sm"
          :class="{
            'text-n-blue-11 font-medium': isOptionSelected(index) && canVote,
            'text-n-slate-12': !isOptionSelected(index) && canVote,
            'text-n-slate-11': !canVote,
          }"
        >
          {{ option }}
        </span>
      </button>
    </div>

    <!-- Vote Button -->
    <div v-if="canVote && selectedOptions.length > 0" class="mt-3">
      <button
        type="button"
        :disabled="!canSubmitVote"
        class="w-full px-4 py-2 text-sm font-medium text-white rounded-lg transition-all"
        :class="{
          'bg-n-blue-9 hover:bg-n-blue-10': canSubmitVote,
          'bg-n-slate-7 cursor-not-allowed': !canSubmitVote,
        }"
        @click="submitVote"
      >
        {{ isVoting ? 'Submitting...' : 'Submit Vote' }}
      </button>
    </div>

    <!-- Voted State -->
    <div
      v-if="hasVoted"
      class="mt-3 flex items-center gap-2 text-sm text-n-green-10"
    >
      <span>✓</span>
      <span>Vote submitted</span>
    </div>

    <!-- Poll Metadata -->
    <div class="flex gap-3 mt-3 text-xs text-n-slate-10">
      <span v-if="pollData.isAnonymous">🔒 Anonymous</span>
      <span v-if="pollData.allowsMultiple">☑ Multiple answers</span>
      <span v-if="pollData.isQuiz">🎯 Quiz</span>
    </div>
  </BaseBubble>
</template>
